// Category CRUD (with a process-lifetime cache) and per-category rule endpoints.

import { apiFetch } from './client';
import type { Category, CategoryType, Rule } from './types';

let categoriesCache: Category[] | null = null;

// Get all categories
export async function getCategories(): Promise<Category[]> {
	if (categoriesCache) {
		return categoriesCache;
	}
	categoriesCache = await apiFetch<Category[]>('/categories');
	return categoriesCache;
}

// Create a new category
export async function createCategory(name: string, sortOrder: number = 0, type?: CategoryType): Promise<Category> {
	const category = await apiFetch<Category>('/categories', {
		method: 'POST',
		body: JSON.stringify({ name, sortOrder, type }),
	});
	categoriesCache = null;
	return category;
}

// Delete a category
export async function deleteCategory(name: string): Promise<void> {
	const encodedName = encodeURIComponent(name);
	await apiFetch(`/categories/${encodedName}`, {
		method: 'DELETE',
	});
	categoriesCache = null;
}

// Update a category (rename and/or change type)
export async function updateCategory(
	name: string,
	updates: { name?: string; type?: CategoryType }
): Promise<void> {
	const encodedName = encodeURIComponent(name);
	await apiFetch(`/categories/${encodedName}`, {
		method: 'PATCH',
		body: JSON.stringify(updates),
	});
	categoriesCache = null;
}

// Rename a category (convenience wrapper)
export async function renameCategory(oldName: string, newName: string): Promise<void> {
	return updateCategory(oldName, { name: newName });
}

// Get rules for a category
export async function getRulesForCategory(categoryName: string): Promise<Rule[]> {
	const encodedName = encodeURIComponent(categoryName);
	return apiFetch<Rule[]>(`/categories/${encodedName}/rules`);
}

// Create a rule for a category
export async function createRule(
	categoryName: string,
	rule: {
		pattern: string;
		matchField?: string;
		matchType?: 'regex' | 'contains';
		priority?: number;
		amountSign?: 'positive' | 'negative' | '';
	}
): Promise<Rule> {
	const encodedName = encodeURIComponent(categoryName);
	return apiFetch<Rule>(`/categories/${encodedName}/rules`, {
		method: 'POST',
		body: JSON.stringify({
			pattern: rule.pattern,
			matchField: rule.matchField || 'description',
			matchType: rule.matchType || 'regex',
			priority: rule.priority || 100,
			amountSign: rule.amountSign || '',
		}),
	});
}

// Delete a rule
export async function deleteRule(categoryName: string, ruleId: string): Promise<void> {
	const encodedName = encodeURIComponent(categoryName);
	const encodedId = encodeURIComponent(ruleId);
	await apiFetch(`/categories/${encodedName}/rules/${encodedId}`, {
		method: 'DELETE',
	});
}
